import React, { useEffect, useRef, useState } from 'react'
import TeamOdd from './TeamOdd.tsx';
import { useBetContext } from '../../../../context/ParlayBetContext.tsx';
import { useUser } from '../../../../context/UserContext.tsx';
import { BetDetails, PlaceWagersResult } from '../../../../models/MyBetModel.tsx';
import { Timestamp } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { logUserActivity } from '../../../../services/player/LoginService.tsx';
import { buildParlayPriceEntries, parseParlayPriceResponse, parlayPrice, placeApiAllWagers, resolveParlaySportsbooks } from '../../../../services/player/apis/Schelude.tsx';
import { useModal } from '../../../../context/ModalContext.tsx';
import { formatNumber } from '../../../../helpers/functions.tsx';
import WagerConfirmedSlip from '../WagerConfirmedSlip.tsx';
import { BetSlipActions, BetSlipChrome, BetSlipMode, BetSlipStake } from '../BetSlipChrome.tsx';
import { useBetContext as useStraightContext } from '../../../../context/BetContext.tsx';
import { useSport } from '../../../../context/SportContext.tsx';
import { convertParlaysToStraight } from '../../../../helpers/betSlipModeSwitch.ts';
import { loadBetSlipContent } from '../../../../helpers/openBetSlipMode.tsx';
import '../../../../styles/StraightQuickSlip.css';

function formatParlaySport(bets: { sport?: string | null }[]) {
    const text = String(bets.find((bet) => bet.sport)?.sport ?? '').trim();
    if (!text) return '';
    return text.replace(/\b([A-Za-z0-9]+)\b/g, (word) => {
        if (word.length <= 4 && word === word.toUpperCase()) return word;
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    });
}

interface IParlayBets {
    setShowLimitAlert: (value: boolean) => void;
    setErrorMessage: (value: string) => void;
}

const ParlayBets = (props: IParlayBets) => {

    const { setShowLimitAlert, setErrorMessage } = props;
    const { bets, deleteAllBets } = useBetContext();
    const { setBets: setStraightBets, bets: straightBets } = useStraightContext();
    const { setIsParlay } = useSport();
    const navigate = useNavigate();
    const { user, updateUser, userIpData } = useUser();
    const [totalBet, setTotalBet] = useState("");
    const [totalReward, setTotalReward] = useState(0);
    const [userBet, setUserBet] = useState<(BetDetails)[]>([]);
    const [showPendingOnly, setShowPendingOnly] = useState(false);
    const [ticketNumber, setTicketNumber] = useState<number | null>(null);
    const [priceParlay, setPriceParlay] = useState<{ price: number } | null>(null);
    const previousValidBets = useRef<string | null>(null);
    const betsRef = useRef(bets);
    betsRef.current = bets;
    const [parlayError, setParlayError] = useState<string | null>(null);
    const [isLoadingParlayPrice, setIsLoadingParlayPrice] = useState(false);
    const [placingBet, setPlacingBet] = useState(false);
    const [confirmedOdds, setConfirmedOdds] = useState<string | null>(null);
    const { closeModal, updateModalContent } = useModal();
    
    // Only pricing-relevant fields — ignore pitchers / odds-array identity churn
    const betsPriceKey = bets
        .map(b => `${b.fixture_id}|${b.odd?.market || b.marketName}|${b.odd?.name || b.betName}|${b.id_selection}|${b.odd?.points ?? ''}|${b.odd?.sportsbook ?? ''}`)
        .join('||');

    useEffect(() => {
        setParlayError(null);
        setPriceParlay(null);

        const currentBets = betsRef.current;
        if (currentBets.length < 2) {
            previousValidBets.current = null;
            setIsLoadingParlayPrice(false);
            return;
        }

        const validBets = buildParlayPriceEntries(currentBets);
        const sportsbooks = resolveParlaySportsbooks(currentBets);

        if (validBets.length < 2) {
            setParlayError('One or more selections are missing required data. Please reselect your picks.');
            setIsLoadingParlayPrice(false);
            return;
        }

        const validBetsKey = [
            sportsbooks.join(','),
            ...validBets.map(b => `${b.fixture_id}-${b.market}-${b.name}`).sort(),
        ].join('|');

        previousValidBets.current = validBetsKey;
        let cancelled = false;
        setIsLoadingParlayPrice(true);

        const fetchPrice = async () => {
            try {
                const response = await parlayPrice(validBets, sportsbooks);
                if (cancelled || previousValidBets.current !== validBetsKey) return;

                const parsed = parseParlayPriceResponse(response, sportsbooks);
                if (!parsed.ok) {
                    setParlayError(parsed.message);
                    setPriceParlay(null);
                    return;
                }

                setParlayError(null);
                setPriceParlay({ price: parsed.price });
            } catch (error) {
                if (cancelled || previousValidBets.current !== validBetsKey) return;
                console.error(error);
                setParlayError('Unable to calculate parlay at this time. Please check your connection and try again.');
                setPriceParlay(null);
            } finally {
                if (!cancelled && previousValidBets.current === validBetsKey) {
                    setIsLoadingParlayPrice(false);
                }
            }
        };

        void fetchPrice();

        return () => { cancelled = true; };
    }, [betsPriceKey]);

    useEffect(() => {
        const numericValue = totalBet === '' ? 0 : parseFloat(totalBet);
        setTotalReward(calculateReward(numericValue));
        
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [priceParlay, totalBet]);

    useEffect(() => {
        const modalElement = document.getElementById('jocksportsModal');
        if (!modalElement) return;
        const onHidden = () => {
            setShowPendingOnly(false);
            setTicketNumber(null);
            setUserBet([]);
            setTotalBet("");
            setTotalReward(0);
            setPlacingBet(false);
            setParlayError(null);
            setPriceParlay(null);
            setIsLoadingParlayPrice(false);
        };
        modalElement.addEventListener('hidden.bs.modal', onHidden);
        return () => modalElement.removeEventListener('hidden.bs.modal', onHidden);
    }, []);

    const handleTotalBetChange = (event: any) => {
        let value = event.target.value;

        if (value.startsWith('0') && value.length > 1 && !value.startsWith('0.')) {
            value = value.replace(/^0+/, '');
        }
        if (/^\d*\.?\d*$/.test(value)) {
            setTotalBet(value);
            const numericValue = value === '' ? 0 : parseFloat(value);
            setTotalReward(calculateReward(numericValue));
        }
    };

    const calculateReward = (betAmount: number) => {
        if (!priceParlay || !priceParlay.price) return 0;

        const priceTotal = priceParlay.price;

        let reward = 0;

        if (priceTotal > 0) {
            reward = (priceTotal / 100) * betAmount;
        } else {
            reward = (100 / Math.abs(priceTotal)) * betAmount;
        }

        return Math.round(reward);
    };

    const getLatestGameDate = (bets: BetDetails[]) => {
        if (!bets.length) return Timestamp.fromDate(new Date());

        return bets.reduce((latest, current) => {
            if (!current.gameDate) return latest;

            return current.gameDate.toMillis() > latest.toMillis()
                ? current.gameDate
                : latest;
        }, bets[0].gameDate || Timestamp.fromDate(new Date()));
    };

    const confirmedBet = async () => {
        if (!user || !user.id) {
            setShowLimitAlert(true);
            setErrorMessage('Please login to place a bet.');
            return;
        }

        if (!priceParlay || parlayError || isLoadingParlayPrice) {
            setShowLimitAlert(true);
            setErrorMessage(
                parlayError ?? 'Parlay odds are not available yet. Please wait or try again.'
            );
            return;
        }

        setPlacingBet(true);

        try {
            const userId = user.id;
            const numericTotalBet = parseFloat(totalBet) || 0;
            const formattedReward = Number(totalReward.toFixed(2));

            const uniqueGameIds = [...new Set(
                bets.map(b => b.gameId).filter(Boolean).map(id => String(id).trim())
            )];

            const sanitizedSelections = bets.map(({ odds, newOdd, ...selection }: any) => selection);

            const parlayDetails: BetDetails = {
                bet: numericTotalBet,
                reward: formattedReward,
                betStatus: 'pending',
                betDate: Timestamp.fromDate(new Date()),
                betType: 'Parlay',
                description: `Parlay - ${bets.length} Teams`,
                gameDate: getLatestGameDate(bets),
                selections: sanitizedSelections,
                gameId: uniqueGameIds[0] || "",
            };

            const result: PlaceWagersResult = await placeApiAllWagers(userId, [parlayDetails], false);

            if (result?.errors?.[0]?.error?.errorType === "oddChange") {
                const message = result.errors[0]?.error?.message
                    || 'Odds have changed. Please review your selections and try again.';
                setShowLimitAlert(true);
                setErrorMessage(message);
                setPriceParlay(null);
                setPlacingBet(false);
                return;
            }

            if (result?.hasErrors) {
                const firstError = result?.errors?.[0]?.error;
                const message = firstError?.message
                    ? `${firstError.message}`
                    : 'There was an error placing one or more bets.';

                setShowLimitAlert(true);
                setErrorMessage(message);
                setPlacingBet(false);
                return;
            }

            const placedBets = (result?.bets?.length ? result.bets : [parlayDetails]) as BetDetails[];
            const placedBet = placedBets[0];
            const totalStake = Number(placedBet.bet ?? numericTotalBet);
            const ticketFromResult = placedBet.ticket ?? null;

            setUserBet(placedBets);
            setTicketNumber(
                ticketFromResult
                    ? Number(String(ticketFromResult).split('-')[0]) || null
                    : null
            );

            updateUser({
                available: parseFloat(((user.available ?? 0) - totalStake).toFixed(2)),
            });

            logUserActivity(
                user.userName,
                `Ticket: ${ticketFromResult ?? ''} Risk: $${totalStake.toFixed(2)} Win: $${Number(placedBet.reward ?? formattedReward).toFixed(2)}`,
                userIpData
            );

            setConfirmedOdds(priceParlay?.price != null ? formatNumber(priceParlay.price) : null);
            setShowPendingOnly(true);
            deleteAllBets();
            setTotalReward(0);
            setTotalBet("");
        } catch (error) {
            console.error(error);
            setShowLimitAlert(true);
            setErrorMessage('There was an error confirming the bet. Please try again.');
        } finally {
            setPlacingBet(false);
        }
    };

    const resetBetState = () => {
        setShowPendingOnly(false);
        setTicketNumber(null);
        setConfirmedOdds(null);
        setUserBet([]);
        setTotalBet("")
        setTotalReward(0);
        closeModal();
    };

    const closeTicket = () => {
        closeModal();
        setPlacingBet(false);
    }

    const handleClearAll = () => {
        deleteAllBets();
        setTotalReward(0);
        setTotalBet("");
        closeModal();
    };

    useEffect(() => {
        if (showPendingOnly || placingBet) return;
        if (bets.length === 0) closeModal();
    }, [bets.length, showPendingOnly, placingBet, closeModal]);

    const bet = userBet.reduce((accumulator, currentValue) => accumulator + Number(currentValue.bet), 0);
    const reward = userBet.reduce((accumulator, currentValue) => accumulator + (currentValue.reward ?? 0), 0);

    const handleTabChange = async (nextMode: BetSlipMode) => {
        if (nextMode !== 'straight') return;
        if (window.innerWidth < 1300) return;

        setStraightBets(convertParlaysToStraight(bets, straightBets));
        setIsParlay(false);
        navigate('/home');
        const content = await loadBetSlipContent('straight', { setShowLimitAlert, setErrorMessage });
        updateModalContent(content);
    };

    const parlayOddsLabel = priceParlay?.price != null
        ? `PARLAY ODDS ${formatNumber(priceParlay.price)}`
        : isLoadingParlayPrice
            ? 'PARLAY ODDS ...'
            : '';

    if (showPendingOnly) {
        const legs = userBet.flatMap((item) => (item.selections?.length ? item.selections : [item]));
        return (
            <div className="bet-slip bet-slip--parlay">
                <WagerConfirmedSlip
                    legs={legs}
                    risk={bet}
                    win={reward}
                    ticketNumber={ticketNumber}
                    onClose={resetBetState}
                    kind="parlay"
                    oddsLabel={confirmedOdds}
                />
            </div>
        );
    }

    return (
        <div className="bet-slip bet-slip--parlay">
            <BetSlipChrome
                pickCount={showPendingOnly ? userBet.length : bets.length}
                mode="parlay"
                onClose={closeTicket}
                onTabChange={handleTabChange}
            />
            {!showPendingOnly && (
                <>
                    <div className="bet-slip__scroll">
                    <div className="sq-confirm__group parlay-slip-group">
                        <div className="sq-confirm__group-head">
                            <div className="sq-confirm__group-copy">
                                <span className="sq-confirm__group-title">
                                    {[`${bets.length}-Team`, formatParlaySport(bets), 'Parlay'].filter(Boolean).join(' ')}
                                </span>
                                <span className="sq-confirm__group-sub">
                                    Parlay • {bets.length} {bets.length === 1 ? 'Selection' : 'Selections'}
                                </span>
                            </div>
                            <span className="sq-confirm__group-picks">
                                {bets.length} {bets.length === 1 ? 'Pick' : 'Picks'}
                            </span>
                        </div>
                        <div className="sq-confirm__group-legs">
                            {bets.map((selection, i) => (
                                <TeamOdd bet={selection} key={i} />
                            ))}
                        </div>
                    </div>
                    <div
                        className={`parlay-error-message ${
                            parlayError
                                ? 'parlay-error-message--error'
                                : isLoadingParlayPrice
                                    ? 'parlay-error-message--loading'
                                    : ''
                        }`}
                        aria-live="polite"
                    >
                        {parlayError
                            ? parlayError
                            : isLoadingParlayPrice
                                ? 'Calculating parlay odds...'
                                : '\u00A0'}
                    </div>
                    </div>
                    <div className="bet-slip__footer">
                        <BetSlipStake
                            focus="risk"
                            onFocusChange={() => {}}
                            risk={totalBet}
                            toWin={isLoadingParlayPrice || parlayError ? '' : totalReward || ''}
                            onRiskChange={handleTotalBetChange}
                            showInputs
                            riskOnly
                            parlayLegs={bets.length}
                            parlayOdds={parlayOddsLabel}
                        />
                        <BetSlipActions
                            mode="parlay"
                            onClear={handleClearAll}
                            onPlace={confirmedBet}
                            placeDisabled={bets.length < 2 || !!parlayError || !priceParlay || isLoadingParlayPrice}
                            placeBusy={placingBet}
                        />
                    </div>
                </>
            )}

        </div>
    );
};

export default ParlayBets;
